import { apolloClient, GET_RECRUITER_LIST, UPDATE_RECRUITER_ROLE } from '~/common/apollo'
import { action, observable, store, computed } from '~/common/mobx.decorator'
import { genericTableStore } from '~/components/generic-table/generic-table.store'
import { useTranslation } from '@opus/web.core.hooks.use-translation'
import { Chip, Typography, useTheme, Box, IconButton, Button, Grid } from '@material-ui/core'
import { HEADER_SORT_DATA_TYPE, LIMIT_DATA_INNER_TABLE, PATHS } from '~/common/constants'
import { UserTitle } from '~/components/user-title/user-title.component'
import React from 'react'
import { masterStore, notifyStore, authStore, routerStore } from '~/stores'
import { RoleCell } from '~/features/recruiter-role/component/role-cell/role-cell.component'
import { UserSettingSvg } from '~/components/icons'
import { uniqBy } from 'lodash'
import { autoFieldTagStyle } from '~/features/recruiter-role/recruiter-role.style'
import { UserAvatar } from '~/components/user-avatar/user-avatar.component'

// Helper function to create fullName safely
const createFullName = (firstName, lastName) => {
	const first = firstName || ''
	const last = lastName || ''
	return [first, last].filter(Boolean).join(' ').trim() || 'Unknown User'
}

@store()
class RecruiterRoleStore {
	@observable recruiters = []
	@observable pageInfo
	@observable openPopup = false
	@observable popupData = {}
	@observable employerKeyword = ''
	@observable employerOptions = []
	@observable isImpersonating = false
	@observable impersonatedRecruiter = null

	@computed
	get hasNextPage() {
		return this.pageInfo?.hasNextPage || false
	}

	@computed
	get endCursor() {
		return this.pageInfo?.endCursor
	}

	@computed
	get filterFields() {
		return genericTableStore.getFiltersValueForTable('role')
	}

	@computed
	get sortOrder() {
		return genericTableStore.sortOrder
	}

	@computed
	get sortField() {
		return genericTableStore.sortField
	}

	@computed
	get impersonatedRecruiterId() {
		return localStorage.getItem('impersonateRecruiterId')
	}

	@action
	setEmployerKeyword = (keyword) => {
		this.employerKeyword = keyword
	}

	@action
	togglePopup = () => {
		this.openPopup = !this.openPopup
	}

	@action
	updatePopupData(data) {
		this.popupData = data
		this.employerKeyword = ''
		const defaultData = data?.supporters?.map((item) => ({ ...item, label: `${item.firstName} ${item.lastName}`, value: item.id }))
		this.employerOptions = uniqBy([...defaultData, ...this.employerOptions], (worker) => worker.id)
	}

	get tableHeader() {
		const { t } = useTranslation()
		const theme = useTheme()

		const COLUMNS = [
			{
				key: 'fullName',
				dataKey: 'fullName',
				title: t('Name'),
				resizable: false,
				flexGrow: 1,
				minWidth: 350,
				sortable: true,
				sortDataType: HEADER_SORT_DATA_TYPE.alphabet,
				cell: ({ rowData }) => {
					return <UserTitle disableClick useForJobApplicant={false} color={theme.palette.primary.main} data={rowData} />
				},
			},
			{
				key: 'roles',
				dataKey: 'roles',
				title: t('ROLE'),
				resizable: false,
				flexGrow: 1,
				minWidth: 200,
				sortable: true,
				sortDataType: HEADER_SORT_DATA_TYPE.multiSelect,
				sortOptions: masterStore?.recruiterFullRoles,
				cell: ({ rowData }) => (
					<Box display="flex" alignItems="center" height="100%">
						<RoleCell data={rowData?.roles} />
					</Box>
				),
			},
			{
				key: 'supporters',
				dataKey: 'supporters',
				title: t('TEMPORARY_SUPPORT'),
				resizable: false,
				flexGrow: 1.7,
				minWidth: 400,
				sortable: false,
				cell: ({ rowData }) => {
					const data = rowData?.supporters
					if (!data?.length) return ''

					// Hiển thị tất cả nếu có 3 người trở xuống
					if (data?.length <= 3) {
						return (
							<Box display="flex" height="100%" alignItems="center">
								{data?.map((item, index) => (
									<Chip
										key={index}
										style={{ border: 'none' }}
										label={
											<div css={autoFieldTagStyle}>
												<UserAvatar
													size={16}
													fontSize={8}
													customHeight={'25px'}
													customWidth={'25px'}
													firstName={item?.firstName}
													lastName={item?.lastName}
													src={item?.fileUrl}
												/>
												<Typography variant="body3">{`${item?.firstName} ${item?.lastName}`}</Typography>
											</div>
										}
									/>
								))}
							</Box>
						)
					}
					// Hiển thị 3 người đầu tiên + số người còn lại
					else {
						const splitData = data?.slice(0, 3)
						const remainingCount = data.length - 3

						return (
							<Box position="relative" width="100%" height="100%">
								<Grid container spacing={0} style={{ height: '100%', alignItems: 'center' }}>
									<Grid item xs={10}>
										<Box display="flex" alignItems="center" height="100%" style={{ overflow: 'hidden' }}>
											<Box display="flex" style={{ flexWrap: 'nowrap', overflow: 'hidden', width: '100%', height: '100%', alignItems: 'center' }}>
												{splitData?.map((item, index) => (
													<Chip
														key={index}
														style={{
															border: 'none',
															margin: '0 2px',
															maxWidth: '32%',
															overflow: 'hidden',
														}}
														label={
															<div css={autoFieldTagStyle} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
																<UserAvatar
																	size={16}
																	fontSize={8}
																	customHeight={'25px'}
																	customWidth={'25px'}
																	firstName={item?.firstName}
																	lastName={item?.lastName}
																	src={item?.fileUrl}
																/>
																<Typography variant="body3" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
																	{`${item?.firstName} ${item?.lastName}`}
																</Typography>
															</div>
														}
													/>
												))}
											</Box>
										</Box>
									</Grid>
									<Grid item xs={2} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												border: '1px solid #E6E6E6',
												borderRadius: '50%',
												width: '28px',
												height: '28px',
												fontSize: '12px',
												marginRight: '10px',
											}}
										>
											+{remainingCount}
										</div>
									</Grid>
								</Grid>
							</Box>
						)
					}
				},
			},
			{
				key: 'action',
				dataKey: 'action',
				title: t('ACTION'),
				width: 120,
				resizable: false,
				sortable: false,
				align: 'center',
				permission: true,
				cell: ({ rowData }) => {
					if (!rowData?.availableToChangeRoles || !authStore.canAccessRoleScreen) {
						return null
					}

					return (
						<Box display="flex" justifyContent="center" alignItems="center" height="100%">
							<IconButton
								size="small"
								title={t('Assign role')}
								onClick={() => {
									this.togglePopup()
									this.updatePopupData(rowData)
								}}
							>
								<UserSettingSvg />
							</IconButton>
						</Box>
					)
				},
			},
			{
				key: 'viewAsRecruiter',
				dataKey: 'viewAsRecruiter',
				title: '',
				resizable: false,
				align: 'center',
				minWidth: 150,
				flexGrow: 0.75,
				cell: ({ rowData }) => {
					// Only show for ADMIN and MANAGER roles
					if (!authStore.isAdmin && !authStore.isManager) {
						return null
					}

					// Don't show button for non-recruiter roles
					if (!rowData.roles.some((role) => role.scope === 'maestro_recruiter')) {
						return null
					}

					return (
						<Box display="flex" justifyContent="center" alignItems="center" height="100%">
							<Button variant="containedPrimary" type="button" size="small" onClick={() => this.startImpersonation(rowData.id)}>
								{t('View as Recruiter')}
							</Button>
						</Box>
					)
				},
			},
		]

		return COLUMNS
	}

	@action
	resetFetchedData = () => {
		this.recruiters = []
		this.pageInfo = null
	}

	@action
	initializeDefaultSort = () => {
		// Force reset sort to default for this tab
		genericTableStore.onUpdateSort('fullName', 'asc')
	}

	@action
	updateRole = async (id, role, supporterIds) => {
		try {
			const variables = authStore.canUpdateUserRole
				? {
						id,
						roles: [role],
						supporterIds,
				  }
				: {
						id,
						supporterIds,
				  }

			await apolloClient().mutate({
				mutation: UPDATE_RECRUITER_ROLE,
				variables,
			})

			this.togglePopup()
			await this.fetchRecruiters()
			const roleName = masterStore.recruiterRoles.find((item) => item.value === role)?.label

			const successMessage = authStore.canUpdateUserRole ? (
				<div>
					You have successfully assign <b>{this.popupData.fullName}</b> to <b>{roleName}</b>
				</div>
			) : (
				<div>
					You have successfully updated temporary support for <b>{this.popupData.fullName}</b>
				</div>
			)

			notifyStore.success(successMessage)
		} catch (e) {
			console.error(e)
		}
	}

	@action
	fetchAllRecruiters = async () => {
		const response = await apolloClient().query({
			query: GET_RECRUITER_LIST,
		})
		const defaultData = response?.data?.recruiters?.nodes?.map((item) => ({ ...item, label: createFullName(item.firstName, item.lastName), value: item.id }))
		this.employerOptions = [...defaultData]
	}

	@action
	fetchRecruiters = async (fullName, additionalFilter = {}) => {
		const sortBy = (this.sortOrder && {
			field: this.sortField || 'fullName',
			order: this.sortOrder || 'desc',
		}) || {
			field: 'fullName',
			order: 'asc',
		}

		const filter = {
			...this.filterFields,
			...(fullName && { keyword: fullName }),
			...additionalFilter,
		}

		const response = await apolloClient().query({
			query: GET_RECRUITER_LIST,
			variables: {
				first: LIMIT_DATA_INNER_TABLE,
				sortBy,
				filter,
			},
		})

		this.recruiters = response?.data?.recruiters?.nodes?.map((item) => ({
			...item,
			fullName: createFullName(item.firstName, item.lastName),
		}))
		this.pageInfo = response?.data?.recruiters?.pageInfo
	}

	@action
	onScrollToEnd = async () => {
		const sortBy = (this.sortOrder && {
			field: this.sortField,
			order: this.sortOrder,
		}) || {
			field: 'fullName',
			order: 'asc',
		}

		const filter = Object.keys(this.filterFields).length && this.filterFields

		const response = await apolloClient().query({
			query: GET_RECRUITER_LIST,
			variables: {
				first: 10,
				sortBy,
				after: this.endCursor,
				filter: { ...filter },
				...(this.endCursor && { after: this.endCursor }),
			},
		})

		this.pageInfo = response?.data?.recruiters?.pageInfo
		this.recruiters = [...this.recruiters, ...response?.data?.recruiters?.nodes?.map((item) => ({ ...item, fullName: createFullName(item.firstName, item.lastName) }))]
	}

	@action
	startImpersonation = async (recruiterId) => {
		// Fetch recruiter details before starting impersonation
		const response = await apolloClient().query({
			query: GET_RECRUITER_LIST,
			variables: {
				filter: { id: recruiterId },
			},
		})

		const recruiter = response?.data?.recruiters?.nodes?.[0]
		if (recruiter) {
			this.impersonatedRecruiter = recruiter
			localStorage.setItem('impersonateRecruiterId', recruiterId)
			localStorage.setItem('impersonatedRecruiter', JSON.stringify(recruiter))
			this.isImpersonating = true
			await routerStore.goPage(PATHS.recruiter.home)
			window.location.reload()
		}
	}

	@action
	stopImpersonation = () => {
		localStorage.removeItem('impersonateRecruiterId')
		localStorage.removeItem('impersonatedRecruiter')
		this.impersonatedRecruiter = null
		this.isImpersonating = false
		window.location.reload()
	}

	@action
	initializeImpersonation = () => {
		const impersonateId = localStorage.getItem('impersonateRecruiterId')
		const savedRecruiter = localStorage.getItem('impersonatedRecruiter')
		if (impersonateId && savedRecruiter) {
			this.isImpersonating = true
			this.impersonatedRecruiter = JSON.parse(savedRecruiter)
		}
	}
}

export const recruiterRoleStore = new RecruiterRoleStore()
